/**
 * Auditoria Fiscal — visão consolidada de 6 meses.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Flag, AlertTriangle, CheckCircle2, ShieldAlert, Loader2, Clock, Search } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import { normalizarPagina } from "./PaginaUI";
import { ReformaTributariaCard, type Indicador80 } from "./ReformaTributariaCard";
import { VisualizadorPaginas } from "./VisualizadorPaginas";

interface Props {
  condominioId: number;
  onVoltar: () => void;
}

interface PagamentoDetectado {
  tipo: string;
  balancete: string;
  fonte_pagina: string | null;
  competencia_id: number | null;
  paginas_extra: number[];
  data_pagamento: string | null;
  valor: number | null;
  detalhe: string | null;
}
interface LinhaAuditoria {
  numero: string;
  prestador_nome: string | null;
  data_emissao: string | null;
  mes_emissao: string | null;
  balancete_origem: string;
  fonte_pagina: string | null;
  competencia_id: number | null;
  valor_servico: number | null;
  federal_valor: number | null;
  federal_pagamentos: PagamentoDetectado[];
  federal_status: string;
  federal_duplicidade: boolean;
  municipal_valor: number | null;
  municipal_pagamentos: PagamentoDetectado[];
  municipal_status: string;
  municipal_duplicidade: boolean;
  federal_alerta?: string | null;       // 0.1.92: PIS/Cofins retido em 2027 (Reforma)
}
interface Response {
  condominio_id: number;
  periodo_inicial: string;
  periodo_final: string;
  total_linhas: number;
  linhas: LinhaAuditoria[];
  total_duplicidades_federal: number;
  total_duplicidades_municipal: number;
  total_atrasados_federal: number;
  total_atrasados_municipal: number;
  total_pago_atraso_federal: number;
  total_pago_atraso_municipal: number;
  regra_2027_ligada?: boolean;
  total_retencao_indevida?: number;
  aviso_reforma?: string | null;
  indicador_80?: Indicador80 | null;
}

function fmtBRL(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a.slice(2)}`;
}
// "2026-01" -> "01/26" (rótulo curto do balancete); outros textos -> undefined
function fmtBalancete(b: string | null | undefined): string | undefined {
  if (!b) return undefined;
  const m = /^(\d{4})-(\d{2})$/.exec(b);
  return m ? `${m[2]}/${m[1].slice(2)}` : undefined;
}

// Status reduzido a um chip compacto
function StatusChip({ status }: { status: string }) {
  const map: Record<string, { cls: string; txt: string; icon?: string }> = {
    PAGO:       { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", txt: "Pago" },
    PAGO_COM_ATRASO: { cls: "bg-amber-100 text-amber-900 border-amber-300",  txt: "Pago c/ atraso" },
    ATRASADO:   { cls: "bg-rose-100 text-rose-800 border-rose-200",          txt: "Atrasado" },
    A_VENCER:   { cls: "bg-amber-100 text-amber-800 border-amber-200",       txt: "A vencer" },
    DUPLICADO:  { cls: "bg-rose-600 text-white border-rose-700",             txt: "🚩 DUP" },
    ISENTO:     { cls: "bg-slate-100 text-slate-500 border-slate-200",       txt: "—" },
  };
  const cfg = map[status] || map.ISENTO;
  return (
    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-semibold whitespace-nowrap ${cfg.cls}`}>
      {cfg.txt}
    </span>
  );
}

// Botão-lupa: abre a página no Visualizador (precisa de competencia_id + página).
// evidencia=true → estilo cinza (página citada na justificativa, ex.: e-mail).
function LupaPagina({
  comp, fonte, label, onVer, evidencia,
}: {
  comp: number | null;
  fonte: string | number | null;
  label?: string;
  onVer: (comp: number, pagina: number) => void;
  evidencia?: boolean;
}) {
  const pag = normalizarPagina(fonte);
  if (comp == null || pag == null) return null;
  const cls = evidencia
    ? "border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100"
    : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300";
  return (
    <button
      type="button"
      onClick={() => onVer(comp, pag)}
      className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded border ${cls} text-[10px] font-medium`}
      title={evidencia
        ? `Evidência citada na justificativa — página ${pag} (ex.: e-mail que lista as NFs do pagamento)`
        : `Ver a página ${pag}${label ? ` (${label})` : ""} do balancete`}
    >
      <Search size={9} /> {label ? `${label} ` : ""}p.{pag}
    </button>
  );
}

// Célula compacta: valor + status + pagamentos (tooltip com detalhes)
function CellTributo({
  valor, status, pgtos, onVer,
}: {
  valor: number | null;
  status: string;
  pgtos: PagamentoDetectado[];
  onVer: (comp: number, pagina: number) => void;
}) {
  if (valor == null && pgtos.length === 0) {
    return <span className="text-slate-300">—</span>;
  }
  const tooltip = pgtos.map(p =>
    `${p.tipo} · ${p.balancete}${p.fonte_pagina ? ` (${p.fonte_pagina})` : ""}${p.data_pagamento ? ` · pago ${fmtData(p.data_pagamento)}` : ""}${p.detalhe ? ` · ${p.detalhe}` : ""}`
  ).join("\n");
  const temLupa = pgtos.some(
    p => p.competencia_id != null &&
      (normalizarPagina(p.fonte_pagina) != null || (p.paginas_extra ?? []).length > 0),
  );

  return (
    <div className="flex flex-col items-end gap-0.5 text-right" title={tooltip || undefined}>
      <span className="tabular-nums text-xs font-medium text-slate-800">{fmtBRL(valor)}</span>
      <StatusChip status={status} />
      {temLupa ? (
        <div className="flex flex-wrap justify-end gap-0.5">
          {pgtos.map((p, i) => (
            <span key={i} className="inline-flex items-center gap-0.5">
              <LupaPagina
                comp={p.competencia_id}
                fonte={p.fonte_pagina}
                label={fmtBalancete(p.balancete)}
                onVer={onVer}
              />
              {(p.paginas_extra ?? []).map((pg, j) => (
                <LupaPagina
                  key={j}
                  comp={p.competencia_id}
                  fonte={pg}
                  onVer={onVer}
                  evidencia
                />
              ))}
            </span>
          ))}
        </div>
      ) : pgtos.length > 0 ? (
        <span className="text-[10px] text-slate-500">{pgtos.length}× pgto</span>
      ) : null}
    </div>
  );
}

function StatTopo({ valor, cor, label }: { valor: number; cor: "rose" | "amber"; label: string }) {
  if (valor === 0) return null;
  const bg = cor === "rose" ? "bg-rose-500/90" : "bg-amber-500/90";
  return (
    <div className={`${bg} rounded px-2 py-1 text-white inline-flex items-center gap-1.5 text-[11px]`}>
      <span className="text-base font-bold">{valor}</span>
      <span className="opacity-90">{label}</span>
    </div>
  );
}

export function AuditoriaFiscalPage({ condominioId, onVoltar }: Props) {
  const { data, isLoading, error } = useQuery<Response>({
    queryKey: ["auditoria-fiscal", condominioId],
    queryFn: () => api.get<Response>(`/condominios/${condominioId}/auditoria-fiscal?meses=6`),
  });

  // Visualizador de páginas — cada linha/pgto pode ser de um balancete diferente.
  const [ver, setVer] = useState<{ comp: number; pag: number } | null>(null);
  const abrir = (comp: number, pag: number) => setVer({ comp, pag });

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      {ver && (
        <VisualizadorPaginas
          competenciaId={ver.comp}
          paginas={[ver.pag]}
          aberto={true}
          onFechar={() => setVer(null)}
        />
      )}
      <button
        onClick={onVoltar}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-4 text-white shadow-sm mb-4">
        <div className="flex items-center gap-3">
          <ShieldAlert size={26} />
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Auditoria Fiscal — últimos 6 meses</h2>
            {data && (
              <p className="text-xs text-slate-200">
                {data.periodo_inicial} a {data.periodo_final} · {data.total_linhas} NFs com retenção
              </p>
            )}
          </div>
          {data && (
            <div className="flex gap-2 flex-wrap">
              <StatTopo valor={data.total_duplicidades_federal + data.total_duplicidades_municipal} cor="rose" label="duplicidades" />
              <StatTopo valor={data.total_atrasados_federal + data.total_atrasados_municipal} cor="rose" label="atrasados" />
              <StatTopo valor={(data.total_pago_atraso_federal ?? 0) + (data.total_pago_atraso_municipal ?? 0)} cor="amber" label="pagos com atraso" />
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" /> Carregando auditoria...
        </div>
      )}
      {error && <div className="text-rose-600 text-sm">Erro ao carregar.</div>}

      {data && (
        <ReformaTributariaCard ligada={!!data.regra_2027_ligada} indicador={data.indicador_80 ?? null}
          indevidas={data.total_retencao_indevida ?? 0} aviso={data.aviso_reforma ?? null} />
      )}

      {data && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[7%]" />     {/* NF nº */}
              <col className="w-[28%]" />    {/* Prestador */}
              <col className="w-[8%]" />     {/* Emissão */}
              <col className="w-[6%]" />     {/* PDF */}
              <col className="w-[11%]" />    {/* Serviço */}
              <col className="w-[20%]" />    {/* Federal */}
              <col className="w-[20%]" />    {/* Municipal */}
            </colgroup>
            <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-2 py-2 text-left">NF</th>
                <th className="px-2 py-2 text-left">Prestador</th>
                <th className="px-2 py-2 text-left">Emissão</th>
                <th className="px-2 py-2 text-left">PDF</th>
                <th className="px-2 py-2 text-right">Serviço</th>
                <th className="px-2 py-2 text-right">Federal</th>
                <th className="px-2 py-2 text-right">Municipal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.linhas.map((l, k) => {
                const flagAny = l.federal_duplicidade || l.municipal_duplicidade;
                return (
                  <tr key={k} className={flagAny ? "bg-rose-50/40" : (k % 2 === 0 ? "bg-white" : "bg-slate-50/30")}>
                    <td className="px-2 py-2 font-semibold tabular-nums">
                      <div className="inline-flex items-center gap-1">
                        {l.numero}
                        {flagAny && <Flag size={11} className="text-rose-600 fill-rose-600" />}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-slate-700 truncate" title={l.prestador_nome ?? ""}>
                      {l.prestador_nome ?? "—"}
                    </td>
                    <td className="px-2 py-2 tabular-nums text-slate-600 text-xs">{fmtData(l.data_emissao)}</td>
                    <td className="px-2 py-2 text-xs text-slate-500">
                      <div className="flex flex-col gap-0.5 items-start">
                        <span>{l.balancete_origem}</span>
                        <LupaPagina comp={l.competencia_id} fonte={l.fonte_pagina} onVer={abrir} />
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-700">{fmtBRL(l.valor_servico)}</td>
                    <td className="px-2 py-2">
                      <CellTributo valor={l.federal_valor} status={l.federal_status} pgtos={l.federal_pagamentos} onVer={abrir} />
                      {l.federal_alerta && (
                        <p className="mt-1 text-[10px] leading-snug text-rose-700 text-right" title={l.federal_alerta}>
                          Reforma: PIS/Cofins retido em 2027
                        </p>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <CellTributo valor={l.municipal_valor} status={l.municipal_status} pgtos={l.municipal_pagamentos} onVer={abrir} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> Pago</span>
        <span className="inline-flex items-center gap-1"><AlertTriangle size={11} className="text-rose-600" /> Atrasado</span>
        <span className="inline-flex items-center gap-1"><Clock size={11} className="text-amber-600" /> A vencer (prazo futuro)</span>
        <span className="inline-flex items-center gap-1"><Flag size={11} className="text-rose-600 fill-rose-600" /> Duplicado (revisar)</span>
        <span className="inline-flex items-center gap-1"><Search size={11} className="text-indigo-600" /> Clique no <b>p.N</b> para abrir a página no balancete</span>
        <span className="text-slate-400">· Passe o mouse na coluna Federal/Municipal para ver detalhes dos pagamentos</span>
      </div>
    </div>
  );
}
// Visualizador de páginas integrado (lupa por linha/pagamento) — 0.1.6
